// Multi-entity G6 (decision O-10): a person employed by two companies punches
// one scanner. Their attendance days — and so overtime — belong to the PRIMARY
// employment unless HR attributes days to another employment by site (the
// scanner the day's punches came from) and/or weekday. One day, one
// employment: hours are never counted twice. Hidden when the viewer may not
// manage attendance rules (the server answers 403).
import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { CalendarClock, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  createAttendanceEngagementRule, deleteAttendanceEngagementRule, listAttendanceEngagementRules, listAttendanceSites,
  type AttendanceEngagementRule,
} from '@/services/attendanceService';

export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

/** "Saturdays and Sundays at EV-GATE". Pure. */
export function ruleLabel(r: Pick<AttendanceEngagementRule, 'device_id' | 'weekdays'>, siteName?: string): string {
  const days = (r.weekdays ?? []).slice().sort((a, b) => a - b).map((d) => WEEKDAYS[d]).join(', ');
  const site = r.device_id ? `at ${siteName || r.device_id}` : '';
  if (days && site) return `${days} ${site}`;
  return days ? `${days} (any site)` : `Days ${site}`;
}

interface Props {
  personId: string;
  engagementId: string;
  company: string;
}

export const AttendanceRulesEditor: React.FC<Props> = ({ personId, engagementId, company }) => {
  const confirm = useConfirm();
  const [rules, setRules] = useState<AttendanceEngagementRule[] | null>(null);
  const [sites, setSites] = useState<{ device_id: string; name: string }[]>([]);
  const [adding, setAdding] = useState(false);
  const [days, setDays] = useState<number[]>([]);
  const [site, setSite] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const all = await listAttendanceEngagementRules(personId);
      setRules(all.filter((r) => r.engagement_id === engagementId));
    } catch {
      setRules(null); // not allowed / unavailable: show nothing
    }
  }, [personId, engagementId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (rules === null) return null;
  const siteName = (id: string | null) => sites.find((s) => s.device_id === id)?.name;

  const openAdd = async () => {
    setAdding(true);
    setDays([]);
    setSite('');
    try {
      setSites(await listAttendanceSites());
    } catch {
      setSites([]);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await createAttendanceEngagementRule({ employee_id: personId, engagement_id: engagementId,
        device_id: site || null, weekdays: days });
      toast.success(`Attendance rule saved for ${company}.`);
      setAdding(false);
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not save the attendance rule.'), { duration: 7000 });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r: AttendanceEngagementRule) => {
    const ok = await confirm({
      title: 'Remove attendance rule',
      message: `${ruleLabel(r, siteName(r.device_id))} will count for the primary employment again.`,
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteAttendanceEngagementRule(r.id);
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not remove the rule.'));
    }
  };

  return (
    <div className="space-y-1" data-testid="attendance-rules">
      <p className="text-xs text-muted-foreground flex items-center gap-1">
        <CalendarClock className="w-3.5 h-3.5" />
        {rules.length === 0
          ? `Attendance counts for the primary employment. Add a rule to count some days for ${company}.`
          : `Attendance days counted for ${company}:`}
      </p>
      {rules.map((r) => (
        <div key={r.id} className="flex items-center gap-2 text-sm text-foreground" data-testid="attendance-rule">
          <span>{ruleLabel(r, siteName(r.device_id))}</span>
          <Button size="sm" variant="ghost" aria-label="Remove rule" onClick={() => void remove(r)}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      ))}
      {!adding ? (
        <Button size="sm" variant="outline" className="gap-1" onClick={() => void openAdd()}>
          <Plus className="w-3.5 h-3.5" /> Attendance rule
        </Button>
      ) : (
        <div className="rounded-md border border-border p-2 space-y-2">
          <div className="flex flex-wrap gap-1" role="group" aria-label="Weekdays">
            {WEEKDAYS.map((w, i) => (
              <button key={w} type="button" aria-pressed={days.includes(i)}
                onClick={() => setDays((d) => (d.includes(i) ? d.filter((x) => x !== i) : [...d, i]))}
                className={`px-2 py-0.5 rounded border text-xs ${days.includes(i) ? 'border-info bg-info-soft text-info-strong' : 'border-border text-muted-foreground'}`}>
                {w}
              </button>
            ))}
          </div>
          <select aria-label="Site" value={site} onChange={(e) => setSite(e.target.value)}
            className="h-8 rounded-md border border-border bg-card px-2 text-sm text-foreground">
            <option value="">Any site</option>
            {sites.map((s) => <option key={s.device_id} value={s.device_id}>{s.name}</option>)}
          </select>
          <div className="flex gap-2">
            <Button size="sm" disabled={busy || (days.length === 0 && !site)} onClick={() => void save()}>Save rule</Button>
            <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
};
